import { CenteredDialog } from '../../components/ui/centered-dialog';
// apps/web/src/pages/monitoramento/registro-form.tsx — Fase 03, Plano 06 (MON-03).
import React, { useState } from 'react';
import { X, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import type { RegistrarConsumoProgressoInput, RegistrarConsumoProgressoResult } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';

interface RegistroFormModalProps {
  osId: string;
  opNumero?: string;
  celulaNome?: string;
  onClose: () => void;
  onSuccess: (resultado: RegistrarConsumoProgressoResult) => void;
}

function mensagemDeErro(valor: unknown): string | null {
  if (typeof valor !== 'object' || valor === null) return null;
  const resposta = valor as Record<string, unknown>;
  return typeof resposta.message === 'string' ? resposta.message : null;
}

function ehResultadoRegistro(valor: unknown): valor is RegistrarConsumoProgressoResult {
  if (typeof valor !== 'object' || valor === null) return false;
  const resultado = valor as Record<string, unknown>;
  return (
    typeof resultado.id === 'string' &&
    typeof resultado.osId === 'string' &&
    typeof resultado.consumoRegistrado === 'number' &&
    (typeof resultado.consumoPrevisto === 'number' || resultado.consumoPrevisto === null) &&
    typeof resultado.sobras === 'number' &&
    typeof resultado.progressoPct === 'number' &&
    typeof resultado.estadoDesvio === 'string' &&
    typeof resultado.gravadoAt === 'string'
  );
}

export const RegistroConsumoProgressoModal: React.FC<RegistroFormModalProps> = ({
  osId,
  opNumero,
  celulaNome,
  onClose,
  onSuccess,
}) => {
  const [consumo, setConsumo] = useState<string>('');
  const [previsto, setPrevisto] = useState<string>('');
  const [sobras, setSobras] = useState<string>('');
  const [progresso, setProgresso] = useState<string>('50');
  const [operadorNome, setOperadorNome] = useState<string>('');
  const [justificativa, setJustificativa] = useState<string>('');

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const consumoNum = parseFloat(consumo);
  // CR-05: baseline vem do consumo previsto informado nesta OS/etapa (não mais um valor fixo).
  // Sem previsto, não há classificação de desvio — o badge simplesmente não aparece.
  const previstoNum = parseFloat(previsto);
  const temPrevisto = !Number.isNaN(previstoNum);
  const isAcima = temPrevisto && !Number.isNaN(consumoNum) && consumoNum > previstoNum;
  const isAbaixo = temPrevisto && !Number.isNaN(consumoNum) && consumoNum < previstoNum;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consumo || Number.isNaN(consumoNum)) {
      setErrorMsg('Informe a quantidade de consumo registrada.');
      return;
    }

    setSaving(true);
    setErrorMsg(null);

    const payload: RegistrarConsumoProgressoInput = {
      osId,
      consumoRegistrado: consumoNum,
      consumoPrevisto: temPrevisto ? previstoNum : undefined,
      sobras: sobras ? parseFloat(sobras) : 0,
      progressoPct: parseFloat(progresso) || 0,
      operadorNome: operadorNome.trim() || undefined,
      justification: justificativa.trim() || undefined,
      identificadorIdempotente: `reg-${osId}-${Date.now()}`,
    };

    try {
      const res = await apiMutate('/api/v1/monitoramento/registro', {
        method: 'POST',
        body: payload,
      });

      if (!res.ok) {
        const json: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDeErro(json) ?? 'Falha ao registrar consumo/progresso');
      }

      const result: unknown = await res.json();
      if (!ehResultadoRegistro(result)) throw new Error('Resposta inválida ao registrar consumo/progresso');
      onSuccess(result);
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Erro na requisição. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CenteredDialog title="Registrar Consumo & Progresso" onClose={onClose}>
      <div className="bg-white rounded-xl shadow-xl border w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Registrar Consumo & Progresso (MON-03)
            </h2>
            <p className="text-xs text-slate-500 font-mono">
              {opNumero ?? 'OP'} {celulaNome ? `— ${celulaNome}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(event) => void handleSubmit(event)} className="p-5 space-y-4 text-xs">
          {errorMsg && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-rose-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Consumo Real */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-slate-700">
                Consumo Registrado (kg / l / un) <span className="text-rose-500">*</span>
              </label>
              {isAcima && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                  <TrendingUp className="w-3 h-3" /> Acima do previsto
                </span>
              )}
              {isAbaixo && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded border border-sky-200">
                  <TrendingDown className="w-3 h-3" /> Abaixo do previsto
                </span>
              )}
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="Ex: 12.5"
              value={consumo}
              onChange={(e) => setConsumo(e.target.value)}
              className="w-full px-3 py-2 border rounded-md font-mono text-sm text-slate-900 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              required
            />
            <p className="text-[11px] text-slate-400">
              Desvios acima ou abaixo do previsto são aceitos e registrados na auditoria sem bloquear.
            </p>
          </div>

          {/* Consumo Previsto (baseline do desvio — CR-05) */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-700" htmlFor="field-registro-form-5870">Consumo Previsto (opcional)</label>
            <input id="field-registro-form-5870"
              type="number"
              step="0.01"
              min="0"
              placeholder="Ex: 10.0"
              value={previsto}
              onChange={(e) => setPrevisto(e.target.value)}
              className="w-full px-3 py-2 border rounded-md font-mono text-sm text-slate-900 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            />
            <p className="text-[11px] text-slate-400">
              Baseline planejado desta OS/etapa. Sem preenchimento, o desvio não é classificado.
            </p>
          </div>

          {/* Sobras */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-700" htmlFor="field-registro-form-6608">Sobras / Aparas (opcional)</label>
            <input id="field-registro-form-6608"
              type="number"
              step="0.01"
              min="0"
              placeholder="Ex: 0.8"
              value={sobras}
              onChange={(e) => setSobras(e.target.value)}
              className="w-full px-3 py-2 border rounded-md font-mono text-sm text-slate-900 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            />
          </div>

          {/* Progresso % */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-slate-700">Progresso da Etapa (%)</label>
              <span className="font-mono font-bold text-slate-900">{progresso}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={progresso}
              onChange={(e) => setProgresso(e.target.value)}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900"
            />
          </div>

          {/* Operador */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-700" htmlFor="field-registro-form-7854">Operador no Turno</label>
            <input id="field-registro-form-7854"
              type="text"
              placeholder="Ex: Carlos Alberto"
              value={operadorNome}
              onChange={(e) => setOperadorNome(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm text-slate-900 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            />
          </div>

          {/* Justificativa */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-700" htmlFor="field-registro-form-8378">Observações / Justificativa</label>
            <input id="field-registro-form-8378"
              type="text"
              placeholder="Opcional..."
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm text-slate-900 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-slate-900 text-white rounded-md font-semibold hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? 'Registrando...' : 'Confirmar Registro'}
            </button>
          </div>
        </form>
      </div>
    </CenteredDialog>
  );
};
